import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { logError } from "@/lib/log";

export const ShareStoryDialog = ({ label, endpoint = "/monthly-story/share", payload, multi = false, onClose }) => {
  const [sups, setSups] = useState(null);
  const [ids, setIds] = useState([]);
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/supporters").then((r) => { const l = r.data.filter((s) => s.email); setSups(l); setIds(l[0] ? [l[0].id] : []); })
      .catch((e) => { logError("Supporters failed:", e); setSups([]); });
  }, []);

  const post = (extra) => api.post(endpoint, { ...payload, message, ...(multi ? { supporter_ids: ids } : { supporter_id: ids[0] }), ...extra });
  const pick = (id) => setIds((l) => (!multi ? [id] : l.includes(id) ? l.filter((x) => x !== id) : l.length >= 5 ? l : [...l, id]));
  const showPreview = async () => {
    setBusy(true);
    try { setPreview((await post({ preview: true })).data.html); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true);
    try { toast.success((await post({})).data.message); onClose(); } catch (e) { toast.error(errMsg(e, "Could not share")); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="share-story-dialog">
        <DialogHeader>
          <DialogTitle>Share your {label}</DialogTitle>
          <DialogDescription>{multi ? "Pick one or more supporters (up to 5 shares a day)." : "Goes to one supporter by email."} Journal entries are never included.</DialogDescription>
        </DialogHeader>
        {sups === null ? <Loader2 className="animate-spin mx-auto" /> : !sups.length ? (
          <p className="text-sm text-mm-muted" data-testid="share-story-no-supporters">Add a supporter with an email first (Settings → Trusted supporters).</p>
        ) : preview ? (
          <>
            <iframe title="Email preview" srcDoc={preview} sandbox="" className="w-full h-80 rounded-xl border bg-white" data-testid="share-story-preview" />
            <div className="flex gap-2">
              <button className="mm-btn-ghost flex-1" onClick={() => setPreview(null)} data-testid="share-story-edit">Edit</button>
              <button className="mm-btn flex-1 inline-flex items-center justify-center gap-2" disabled={busy} onClick={send} data-testid="share-story-send">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send{multi && ids.length > 1 ? ` to ${ids.length}` : ""}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-1.5">
              {sups.map((s) => <button key={s.id} className={`mm-chip !py-1.5 ${ids.includes(s.id) ? "mm-chip-on" : ""}`} onClick={() => pick(s.id)} data-testid={`share-story-supporter-${s.id}`}>{s.name}</button>)}
            </div>
            <Textarea placeholder="Add a short message (optional)" value={message} onChange={(e) => setMessage(e.target.value.slice(0, 300))} rows={3} data-testid="share-story-message" />
            <button className="mm-btn w-full" disabled={busy || !ids.length} onClick={showPreview} data-testid="share-story-preview-button">
              {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : multi && ids.length > 1 ? `Preview email (as ${sups.find((s) => s.id === ids[0])?.name} sees it)` : "Preview email"}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
